import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Library from './pages/Library';
import LibraryManager from './pages/LibraryManager';
import ShoeboxView from './pages/ShoeboxView';
import AlbumView from './pages/AlbumView';
import FlipBookView from './pages/FlipBookView';
import SharedAlbum from './pages/SharedAlbum';
import AdminPanel from './pages/AdminPanel';
import ConnectedAccounts from './pages/ConnectedAccounts';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import logger from './utils/logger';

// Backend URL - smart detection for production
const BACKEND_URL = (() => {
  // For custom domain deployment
  if (window.location.hostname === 'picturebook.co') {
    return 'https://picturebook.co';
  }
  // For preview deployment
  if (window.location.hostname.includes('emergentagent.com')) {
    return 'https://photo-ai-album.preview.emergentagent.com';
  }
  // Fallback to env variable or current origin
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
})();

export const API = BACKEND_URL + '/api';

// Axios interceptors for auth token and logging
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Store request start time for duration calculation
    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging
axios.interceptors.response.use(
  (response) => {
    // Log API call with duration
    if (response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      const endpoint = response.config.url.replace(BACKEND_URL, '');
      logger.logApiCall(response.config.method.toUpperCase(), endpoint, duration, response.status);
    }
    return response;
  },
  (error) => {
    // Log API errors
    if (error.config && error.config.metadata) {
      const duration = Date.now() - error.config.metadata.startTime;
      const endpoint = error.config.url.replace(BACKEND_URL, '');
      const status = error.response ? error.response.status : 'network_error';
      logger.logApiCall(error.config.method.toUpperCase(), endpoint, duration, status);
      logger.error(`API Error: ${endpoint}`, error, {
        status: status,
        response_data: error.response?.data
      });
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    // Check for magic link token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const magicToken = urlParams.get('magic_token');

    if (magicToken) {
      // Verify magic link
      axios
        .post(`${API}/auth/verify-magic-link?token=${magicToken}`)
        .then((response) => {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setIsAuthenticated(true);
          // Remove token from URL
          window.history.replaceState({}, document.title, '/dashboard');
        })
        .catch((error) => {
          console.error('Magic link verification failed:', error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        // Verify token
        axios
          .get(`${API}/auth/me`)
          .then(() => {
            setIsAuthenticated(true);
          })
          .catch(() => {
            localStorage.removeItem('token');
            setIsAuthenticated(false);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="animate-pulse text-xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary name="App">
      <div className="App flex flex-col min-h-screen">
        <BrowserRouter>
          <Header 
            isAuthenticated={isAuthenticated} 
            setIsAuthenticated={setIsAuthenticated}
            onShowAuth={() => setShowAuth(true)}
          />
          <div className="flex-grow">
            <ErrorBoundary name="Routes">
              <Routes>
                <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Landing setIsAuthenticated={setIsAuthenticated} showAuth={showAuth} setShowAuth={setShowAuth} />} />
                <Route path="/dashboard" element={isAuthenticated ? <Dashboard /> : <Navigate to="/" />} />
                <Route path="/library" element={isAuthenticated ? <Library /> : <Navigate to="/" />} />
                <Route path="/library-manager" element={isAuthenticated ? <LibraryManager /> : <Navigate to="/" />} />
                <Route path="/shoebox/:shoeboxId" element={isAuthenticated ? <ShoeboxView /> : <Navigate to="/" />} />
                <Route path="/album/:albumId" element={isAuthenticated ? <AlbumView /> : <Navigate to="/" />} />
                <Route path="/album/:albumId/flipbook" element={isAuthenticated ? <FlipBookView /> : <Navigate to="/" />} />
                <Route path="/flipbook/:albumId" element={isAuthenticated ? <FlipBookView /> : <Navigate to="/" />} />
                <Route path="/share/:shareToken" element={<SharedAlbum />} />
                <Route path="/admin" element={isAuthenticated ? <AdminPanel /> : <Navigate to="/" />} />
                <Route path="/connected-accounts" element={isAuthenticated ? <ConnectedAccounts /> : <Navigate to="/" />} />
              </Routes>
            </ErrorBoundary>
        </div>
          <Footer />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </ErrorBoundary>
  );
}

export default App;
